/**
 * OIMS v2 - Logs tab (Audit Intelligence)
 * Audit log search with pagination, metadata display, and Command Bridge UI
 */

import { escapeHtml, showLoading, hideLoading } from './utils.js';

let logStart, logEnd, logKeyword, logUserOnly, btnSearchLogs;
let logsTableBody, btnLogsPrev, btnLogsNext, logsPagination;
let logsData = [];
let currentLogsPage = 0;
const logsPerPage = 25;

export function initLogs(state) {
  logStart = document.getElementById("logStart");
  logEnd = document.getElementById("logEnd");
  logKeyword = document.getElementById("logKeyword");
  logUserOnly = document.getElementById("logUserOnly");
  btnSearchLogs = document.getElementById("btnSearchLogs");
  logsTableBody = document.getElementById("logsTableBody");
  btnLogsPrev = document.getElementById("btnLogsPrev");
  btnLogsNext = document.getElementById("btnLogsNext");
  logsPagination = document.getElementById("logsPagination");

  btnSearchLogs && btnSearchLogs.addEventListener("click", () => searchLogs(state));

  if (btnLogsPrev) {
    btnLogsPrev.addEventListener("click", () => {
      if (currentLogsPage > 0) {
        currentLogsPage--;
        renderLogsPage(state);
      }
    });
  }

  if (btnLogsNext) {
    btnLogsNext.addEventListener("click", () => {
      const totalPages = Math.ceil(logsData.length / logsPerPage);
      if (currentLogsPage < totalPages - 1) {
        currentLogsPage++;
        renderLogsPage(state);
      }
    });
  }

  // Toggle switch -> hidden select sync
  const toggle = document.getElementById("logUserOnlyToggle");
  if (toggle && logUserOnly) {
    toggle.addEventListener("change", () => {
      logUserOnly.value = toggle.checked ? "true" : "false";
      updateBriefing();
    });
  }

  // Briefing updates on input changes
  if (logStart) logStart.addEventListener("change", updateBriefing);
  if (logEnd) logEnd.addEventListener("change", updateBriefing);
  if (logKeyword) logKeyword.addEventListener("input", updateBriefing);

  // Toolbar
  const btnExport = document.getElementById("btnExportLogs");
  const btnClear = document.getElementById("btnClearResults");
  if (btnExport) btnExport.addEventListener("click", exportLogsCSV);
  if (btnClear) btnClear.addEventListener("click", () => clearResults(state));

  updateBriefing();
}

function setLogsStatus(text, color = "secondary") {
  const badge = document.getElementById("logsStatusBadge");
  if (!badge) return;
  badge.textContent = text;
  const variantMap = { secondary: "idle", info: "running", success: "success", danger: "error", primary: "running", warning: "running" };
  badge.className = `status-badge ${variantMap[color] || "idle"}`;
}

function updateBriefing() {
  const briefingText = document.getElementById("logsBriefingText");
  if (!briefingText) return;

  const startVal = logStart?.value;
  const endVal = logEnd?.value;
  const keyword = logKeyword?.value?.trim();
  const myOnly = logUserOnly?.value === "true";

  if (startVal && endVal) {
    let msg = `<span class="briefing-ready"><i class="bi bi-check-circle me-1"></i>Ready: <strong>${escapeHtml(startVal)}</strong> to <strong>${escapeHtml(endVal)}</strong>`;
    if (keyword) msg += ` for "${escapeHtml(keyword)}"`;
    if (myOnly) msg += ` (my logs only)`;
    msg += `</span>`;
    briefingText.innerHTML = msg;
  } else if (startVal || endVal) {
    briefingText.textContent = "Select both start and end dates";
  } else {
    briefingText.textContent = "Select date range and press Search to begin";
  }

  // Update launch button subtitle
  const launchSmall = btnSearchLogs?.querySelector(".launch-text small");
  if (launchSmall) {
    if (startVal && endVal) {
      let sub = `${startVal} → ${endVal}`;
      if (keyword) sub += ` • "${keyword}"`;
      launchSmall.textContent = sub;
    } else {
      launchSmall.textContent = "Query operational event history";
    }
  }
}

function setLaunchState(searching) {
  if (!btnSearchLogs) return;
  if (searching) {
    btnSearchLogs.disabled = true;
    btnSearchLogs.classList.add("executing");
    const icon = btnSearchLogs.querySelector(".launch-icon i");
    if (icon) icon.className = "bi bi-arrow-repeat";
    const strong = btnSearchLogs.querySelector(".launch-text strong");
    if (strong) strong.textContent = "Searching...";
  } else {
    btnSearchLogs.disabled = false;
    btnSearchLogs.classList.remove("executing");
    const icon = btnSearchLogs.querySelector(".launch-icon i");
    if (icon) icon.className = "bi bi-search";
    const strong = btnSearchLogs.querySelector(".launch-text strong");
    if (strong) strong.textContent = "Search Audit Logs";
    updateBriefing();
  }
}

function showSearchMetadata(count, startDate, endDate) {
  const meta = document.getElementById("logsMetadata");
  const countEl = document.getElementById("logsResultCount");
  const rangeEl = document.getElementById("logsDateRange");
  const timeEl = document.getElementById("logsQueryTime");
  if (meta) {
    meta.style.display = "";
    if (countEl) countEl.textContent = `${count} entries`;
    if (rangeEl) rangeEl.textContent = `${startDate} → ${endDate}`;
    if (timeEl) timeEl.textContent = new Date().toLocaleTimeString();
  }
}

function clearResults(state) {
  logsData = [];
  currentLogsPage = 0;
  if (logsTableBody) {
    logsTableBody.innerHTML = `<tr class="logs-empty-state"><td colspan="6" class="text-center py-5">
      <i class="bi bi-shield-check" style="font-size: 3rem; opacity: 0.2;"></i>
      <p class="text-muted mt-3 mb-0">No audit records found. Configure your search parameters and press Search.</p>
    </td></tr>`;
  }
  if (logsPagination) logsPagination.textContent = "";
  if (btnLogsPrev) btnLogsPrev.disabled = true;
  if (btnLogsNext) btnLogsNext.disabled = true;
  const meta = document.getElementById("logsMetadata");
  if (meta) meta.style.display = "none";
  setLogsStatus("Ready", "secondary");
}

function exportLogsCSV() {
  if (!logsData.length) {
    window.toast?.info("No data to export.");
    return;
  }

  const headers = ["Time", "User", "IP", "Action", "Status", "Details"];
  const rows = logsData.map(row => {
    const t = new Date(row.timestamp || Date.now()).toLocaleString();
    const message = row.action || row.message || "-";
    const status = deriveStatus(message, row.reference);
    let details = "-";
    if (row.reference && typeof row.reference === "object") {
      details = JSON.stringify(row.reference);
    }
    return [t, row.user || "-", row.ip || "-", message, status.text, details];
  });

  let csv = headers.join(",") + "\n";
  rows.forEach(r => {
    csv += r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(",") + "\n";
  });

  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  window.toast?.success("Exported to CSV.");
}

function deriveStatus(message, reference) {
  const msg = (message || "").toLowerCase();

  if (msg.includes("failed") || msg.includes("timed out") || msg.includes("cancelled") ||
      msg.includes("error") || msg.includes("forbidden") || msg.includes("unauthorized")) {
    return { text: "Failed", class: "danger" };
  }

  if (msg.includes("completed successfully")) {
    return { text: "Success", class: "success" };
  }

  if (msg.includes("completed with exit code")) {
    const match = msg.match(/exit code (\d+)/);
    if (match) {
      const exitCode = parseInt(match[1]);
      if (exitCode === 0) return { text: "Success", class: "success" };
      else return { text: "Failed", class: "danger" };
    }
  }

  if (msg.includes("success") || msg.includes("completed") || msg.includes("sent") ||
      msg.includes("authenticated") || msg.includes("executed")) {
    return { text: "Success", class: "success" };
  }

  if (msg.includes("starting") || msg.includes("pending")) {
    return { text: "Pending", class: "warning" };
  }

  if (reference && typeof reference === "object") {
    const ssmStatus = (reference.ssm_status || "").toLowerCase();
    const responseCode = reference.response_code;

    if (ssmStatus === "success" && (responseCode === 0 || responseCode === undefined)) {
      return { text: "Success", class: "success" };
    }
    if (ssmStatus === "success" && responseCode !== 0) {
      return { text: "Failed", class: "danger" };
    }
    if (ssmStatus === "failed" || ssmStatus === "timedout" || ssmStatus === "cancelled") {
      return { text: "Failed", class: "danger" };
    }

    const status = (reference.status || "").toLowerCase();
    const error = reference.error || reference.message;

    if (status === "error" || (error && typeof error === "string" && error.toLowerCase().includes("error"))) {
      return { text: "Failed", class: "danger" };
    }
    if (status === "sent" || status === "success") {
      return { text: "Success", class: "success" };
    }
  }

  return { text: "Info", class: "secondary" };
}

function formatAction(message, reference, instanceNames) {
  const msg = (message || "").toLowerCase();

  if (reference && typeof reference === "object") {
    const instanceId = reference.instance_id || reference.instance;
    const action = reference.action || reference.action_id;

    if (action && instanceId) {
      const actionNames = {
        "restart_wildfly": "Restart WildFly",
        "restart_jbpm": "Restart JBPM",
        "restart_birt": "Restart Birt",
        "restart_nginx": "Restart Nginx",
        "reboot_instance": "Reboot Instance"
      };
      const actionName = actionNames[action] || action;
      const instanceName = instanceNames[instanceId] || instanceId;

      let stage = "";
      if (msg.includes("ssm command sent")) stage = " → Sent";
      else if (msg.includes("action executed")) stage = " → Executing";
      else if (msg.includes("completed successfully")) stage = " ✓ Completed";
      else if (msg.includes("failed") || msg.includes("timed out")) stage = " ✗ Failed";

      return `${actionName}: ${instanceName}${stage}`;
    }
  }

  return message || "-";
}

function renderLogsPage(state) {
  const start = currentLogsPage * logsPerPage;
  const end = start + logsPerPage;
  const pageData = logsData.slice(start, end);

  logsTableBody.innerHTML = "";

  if (pageData.length === 0) {
    logsTableBody.innerHTML = `<tr class="logs-empty-state"><td colspan="6" class="text-center py-4">No logs found</td></tr>`;
    return;
  }

  pageData.forEach(row => {
    const tr = document.createElement("tr");
    const dt = new Date(row.timestamp || Date.now());
    const dateStr = dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    const timeStr = dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    const message = row.action || row.message || "-";
    const reference = row.reference || null;

    const action = formatAction(message, reference, state.instanceNames);
    const status = deriveStatus(message, reference);

    let details = "-";
    if (reference && typeof reference === "object") {
      const stdout = reference.stdout;
      const stderr = reference.stderr;

      if (stdout || stderr) {
        details = "";
        if (stdout) details += `<strong>Output:</strong>\n${escapeHtml(stdout)}`;
        if (stderr) {
          if (stdout) details += "\n\n";
          details += `<strong class="text-danger">Errors:</strong>\n${escapeHtml(stderr)}`;
        }
      } else {
        details = escapeHtml(JSON.stringify(reference, null, 2));
      }
    } else if (reference) {
      details = escapeHtml(String(reference));
    }

    tr.innerHTML = `
      <td><span class="log-date">${escapeHtml(dateStr)}</span><span class="log-time">${escapeHtml(timeStr)}</span></td>
      <td class="text-truncate">${escapeHtml(row.user || "-")}</td>
      <td><span class="badge bg-secondary">${escapeHtml(row.ip || "-")}</span></td>
      <td><span class="badge bg-info text-wrap">${action}</span></td>
      <td><span class="badge bg-${escapeHtml(status.class)}">${escapeHtml(status.text)}</span></td>
      <td>
        <div class="log-details-cell">
          <pre style="margin:0; white-space: pre-wrap; word-wrap: break-word;">${details}</pre>
        </div>
      </td>
    `;
    logsTableBody.appendChild(tr);
  });

  const totalPages = Math.ceil(logsData.length / logsPerPage);
  logsPagination.textContent = `Page ${currentLogsPage + 1} of ${totalPages} (${logsData.length} total logs)`;
  btnLogsPrev.disabled = currentLogsPage === 0;
  btnLogsNext.disabled = currentLogsPage >= totalPages - 1;
}

async function searchLogs(state) {
  if (!state.accessToken) {
    window.toast.warning("Please sign in to search logs.");
    return;
  }

  const start_date = logStart.value;
  const end_date = logEnd.value;
  const keyword = logKeyword.value || "";
  const user_only = logUserOnly.value === "true";

  if (!start_date || !end_date) {
    window.toast.warning("Please select start and end dates.");
    return;
  }

  if (new Date(start_date) > new Date(end_date)) {
    window.toast.warning("Start date must be before or equal to end date.");
    return;
  }

  setLaunchState(true);
  setLogsStatus("Searching...", "info");
  logsTableBody.innerHTML = `<tr><td colspan="6" class="text-center py-4"><i class="bi bi-arrow-repeat" style="animation: spin 1s linear infinite; font-size: 1.5rem;"></i><p class="text-muted mt-2 mb-0">Searching audit trail...</p></td></tr>`;
  showLoading("Searching logs...");

  try {
    const tz_offset = new Date().getTimezoneOffset();  // minutes from UTC (e.g. -120 for UTC+2)
    const { data } = await axios.get("/logs/search", {
      params: { start_date, end_date, keyword, user_only, tz_offset }
    });

    let rows = data?.results || data?.logs || [];

    if (!rows.length) {
      logsTableBody.innerHTML = `<tr class="logs-empty-state"><td colspan="6" class="text-center py-5">
        <i class="bi bi-shield-check" style="font-size: 3rem; opacity: 0.2;"></i>
        <p class="text-muted mt-3 mb-0">No results found for this search criteria.</p>
      </td></tr>`;
      logsPagination.textContent = "";
      btnLogsPrev.disabled = true;
      btnLogsNext.disabled = true;
      setLogsStatus("No Results", "warning");
      showSearchMetadata(0, start_date, end_date);
      window.toast.info("No logs found matching your criteria");
      return;
    }

    rows.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

    if (!keyword) {
      const totalBefore = rows.length;
      rows = rows.filter(row => {
        const msg = (row.action || row.message || "").toLowerCase();
        if (msg.includes("→ sent") || msg.includes("→ executing")) return false;
        if (msg.includes("ssm command sent") || msg.includes("action executed (ssm sent)") || msg.includes("backup upload initiated")) return false;
        return true;
      });
      if (rows.length < totalBefore) {
        console.log(`Logs: filtered ${totalBefore - rows.length} intermediate entries`);
      }
    }

    logsData = rows;
    currentLogsPage = 0;
    renderLogsPage(state);
    showSearchMetadata(rows.length, start_date, end_date);
    setLogsStatus(`${rows.length} Found`, "success");
    window.toast.success(`Found ${rows.length} log entries`);

  } catch (e) {
    console.error(e);
    logsTableBody.innerHTML = `<tr class="logs-empty-state"><td colspan="6" class="text-center py-4 text-danger">
      <i class="bi bi-exclamation-triangle" style="font-size: 2rem;"></i>
      <p class="mt-2 mb-0">Error: ${escapeHtml(e.message)}</p>
    </td></tr>`;
    logsPagination.textContent = "";
    setLogsStatus("Error", "danger");
    window.toast.error("Search failed: " + e.message);
  } finally {
    hideLoading();
    setLaunchState(false);
  }
}
